import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ServerConfig, CapsConfig, ServerType, SauceConfig, BrowserStackConfig, TestingBotConfig, CrossBrowserTestingConfig } from '../services/inspectorApi';

interface InspectorState {
  serverConfig: ServerConfig;
  caps: CapsConfig;
  sessionId: string | null;
  isConnected: boolean;
  pageSource: string;
  screenshot: string;
  currentUrl: string;
  activeTab: 'source' | 'screenshot' | 'mobile';
  isLoading: boolean;
  error: string | null;
}

interface InspectorContextType extends InspectorState {
  serverConfig: ServerConfig;
  caps: CapsConfig;
  setServerConfig: (config: ServerConfig | ((prev: ServerConfig) => ServerConfig)) => void;
  setCaps: (caps: CapsConfig) => void;
  setServerType: (type: ServerType) => void;
  setSauceConfig: (config: SauceConfig) => void;
  setBrowserStackConfig: (config: BrowserStackConfig) => void;
  setTestingBotConfig: (config: TestingBotConfig) => void;
  setCrossBrowserTestingConfig: (config: CrossBrowserTestingConfig) => void;
  setActiveTab: (tab: 'source' | 'screenshot' | 'mobile') => void;
  setSessionId: (id: string | null) => void;
  setIsConnected: (connected: boolean) => void;
  setPageSource: (source: string) => void;
  setScreenshot: (screenshot: string) => void;
  setCurrentUrl: (url: string) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  resetSession: () => void;
}

const getDefaultServerConfig = (): ServerConfig => ({
  hostname: '127.0.0.1',
  port: 4723,
  path: '/wd/hub',
  protocol: 'http',
  ssl: false,
  serverType: 'local' as ServerType
});

const getDefaultCaps = (): CapsConfig => ({
  platformName: 'Android',
  deviceName: '',
  automationName: 'UiAutomator2'
});

const initialState: InspectorState = {
  serverConfig: getDefaultServerConfig(),
  caps: getDefaultCaps(),
  sessionId: null,
  isConnected: false,
  pageSource: '',
  screenshot: '',
  currentUrl: '',
  activeTab: 'screenshot',
  isLoading: false,
  error: null
};

const InspectorContext = createContext<InspectorContextType | undefined>(undefined);

export function InspectorProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<InspectorState>(initialState);

  const setServerConfig = useCallback((config: ServerConfig | ((prev: ServerConfig) => ServerConfig)) => {
    setState(prev => {
      if (typeof config === 'function') {
        return { ...prev, serverConfig: config(prev.serverConfig) };
      }
      return { ...prev, serverConfig: config };
    });
  }, []);

  const setCaps = useCallback((caps: CapsConfig) => {
    setState(prev => ({ ...prev, caps }));
  }, []);

  const setServerType = useCallback((type: ServerType) => {
    setState(prev => ({
      ...prev,
      serverConfig: { ...prev.serverConfig, serverType: type }
    }));
  }, []);

  const setSauceConfig = useCallback((config: SauceConfig) => {
    setState(prev => ({
      ...prev,
      serverConfig: { ...prev.serverConfig, sauce: config }
    }));
  }, []);

  const setBrowserStackConfig = useCallback((config: BrowserStackConfig) => {
    setState(prev => ({
      ...prev,
      serverConfig: { ...prev.serverConfig, browserstack: config }
    }));
  }, []);

  const setTestingBotConfig = useCallback((config: TestingBotConfig) => {
    setState(prev => ({
      ...prev,
      serverConfig: { ...prev.serverConfig, testingbot: config }
    }));
  }, []);

  const setCrossBrowserTestingConfig = useCallback((config: CrossBrowserTestingConfig) => {
    setState(prev => ({
      ...prev,
      serverConfig: { ...prev.serverConfig, crossbrowsertesting: config }
    }));
  }, []);

  const setActiveTab = useCallback((tab: 'source' | 'screenshot' | 'mobile') => {
    setState(prev => ({ ...prev, activeTab: tab }));
  }, []);

  const setSessionId = useCallback((id: string | null) => {
    setState(prev => ({ ...prev, sessionId: id }));
  }, []);

  const setIsConnected = useCallback((connected: boolean) => {
    setState(prev => ({ ...prev, isConnected: connected }));
  }, []);

  const setPageSource = useCallback((source: string) => {
    setState(prev => ({ ...prev, pageSource: source }));
  }, []);

  const setScreenshot = useCallback((screenshot: string) => {
    setState(prev => ({ ...prev, screenshot }));
  }, []);

  const setCurrentUrl = useCallback((url: string) => {
    setState(prev => ({ ...prev, currentUrl: url }));
  }, []);

  const setIsLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, isLoading: loading }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  const resetSession = useCallback(() => {
    setState(prev => ({
      ...prev,
      sessionId: null,
      isConnected: false,
      pageSource: '',
      screenshot: '',
      currentUrl: '',
      error: null
    }));
  }, []);

  const value: InspectorContextType = {
    ...state,
    setServerConfig,
    setCaps,
    setServerType,
    setSauceConfig,
    setBrowserStackConfig,
    setTestingBotConfig,
    setCrossBrowserTestingConfig,
    setActiveTab,
    setSessionId,
    setIsConnected,
    setPageSource,
    setScreenshot,
    setCurrentUrl,
    setIsLoading,
    setError,
    resetSession
  };

  return <InspectorContext.Provider value={value}>{children}</InspectorContext.Provider>;
}

export function useInspector() {
  const context = useContext(InspectorContext);
  if (!context) {
    throw new Error('useInspector must be used within an InspectorProvider');
  }
  return context;
}